import axios from "axios";
import * as metrics from "../utils/metrics";

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    "X-Requested-With": "XMLHttpRequest",
  },
  withCredentials: true, // Important for cookie-based auth
});

// Session optional paths - where we don't redirect on 401
const SESSION_OPTIONAL_PATHS = ["/app/shopify/oauth"];

/**
 * Get the brand ID from session storage.
 * @param {string} userId - The user ID
 * @returns {string|null} - The brand ID or null if not found
 */
function getSessionBrandId(userId) {
  try {
    return sessionStorage.getItem(userId);
  } catch (e) {
    console.log("sessionStorage unavailable");
    return null;
  }
}

/**
 * Set the brand ID in session storage.
 * @param {string} userId - The user ID
 * @param {string} brandId - The brand ID to set
 */
function setSessionBrandId(userId, brandId) {
  try {
    sessionStorage.setItem(userId, brandId);
  } catch (e) {
    console.log("sessionStorage unavailable");
  }
}

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Add metrics session header
    config.headers["X-Metrics-Session"] = metrics.getSessionId();

    // Add auth token if exists
    const token = localStorage.getItem("auth_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    // Successful response
    return response;
  },
  (error) => {
    // Handle authentication errors
    if (error.response) {
      const { status } = error.response;
      const currentPath = window.location.pathname;

      // Redirect to login only on authentication errors (401), not authorization errors (403)
      if (status === 401 && !SESSION_OPTIONAL_PATHS.includes(currentPath)) {
        // Clear any stored auth data
        localStorage.removeItem("auth_token");
        sessionStorage.clear();

        // Redirect to login
        window.location.replace("/app/login");
      }
    }

    // Re-throw the error for handling in the calling code
    return Promise.reject(error);
  },
);

/**
 * Set the impersonation header for API requests.
 * @param {string} impersonating - The user ID being impersonated
 */
export function setImpersonateHeader(impersonating) {
  apiClient.defaults.headers.common.impersonating = impersonating;
}

/**
 * Set the brand ID in the request headers.
 * @param {string} userId - Current user ID
 * @param {string} overrideBrandId - Brand ID to override
 * @param {string} fallbackBrandId - Fallback brand ID if none is set
 */
export function setBrandHeader(userId, overrideBrandId, fallbackBrandId) {
  const brandId =
    overrideBrandId || // if set explicitly
    getSessionBrandId(userId) || // otherwise current session's brand ID
    fallbackBrandId; // otherwise fallback brand ID

  apiClient.defaults.headers.common.brand = brandId;
  setSessionBrandId(userId, brandId);
}

/**
 * Set the authorization token in the request headers.
 * @param {string} token - The authorization token
 */
export function setAuthToken(token) {
  if (token) {
    apiClient.defaults.headers.common.Authorization = `Bearer ${token}`;
    localStorage.setItem("auth_token", token);
  } else {
    delete apiClient.defaults.headers.common.Authorization;
    localStorage.removeItem("auth_token");
  }
}

/**
 * Backend API helper methods.
 * These methods wrap axios to provide a consistent interface for making API requests.
 * Uses the `VITE_API_BASE_URL` environment variable as the base URL.
 */
export const api = {
  /**
   * Make a GET request to the backend API.
   * @param {string} url - The API endpoint URL without base URL
   * @param {object} config - Optional axios request configuration
   * @returns {Promise} - Axios response promise
   */
  get: (url, config) => apiClient.get(url, config),

  /**
   * Make a POST request to the backend API.
   * @param {string} url - The API endpoint URL without base URL
   * @param {object} data - The request payload
   * @param {object} config - Optional axios request configuration
   * @returns {Promise} - Axios response promise
   */
  post: (url, data, config) => apiClient.post(url, data, config),

  /**
   * Make a PUT request to the backend API.
   * @param {string} url - The API endpoint URL without base URL
   * @param {object} data - The request payload
   * @param {object} config - Optional axios request configuration
   * @returns {Promise} - Axios response promise
   */
  put: (url, data, config) => apiClient.put(url, data, config),

  /**
   * Make a PATCH request to the backend API.
   * @param {string} url - The API endpoint URL without base URL
   * @param {object} data - The request payload
   * @param {object} config - Optional axios request configuration
   * @returns {Promise} - Axios response promise
   */
  patch: (url, data, config) => apiClient.patch(url, data, config),

  /**
   * Make a DELETE request to the backend API.
   * @param {string} url - The API endpoint URL without base URL
   * @param {object} config - Optional axios request configuration
   * @returns {Promise} - Axios response promise
   */
  delete: (url, config) => apiClient.delete(url, config),
};
